// src/components/Settings.tsx
import React, { useEffect, useState } from "react";
import { Check, Copy, Download, ImageIcon, Upload } from "lucide-react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import useCtx from "../hooks/useCts";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";
import { cacheCertificateLogoFromFile } from "../utils/certificateLogo";

const storage = getStorage();
const firestore = getFirestore();
const isDirectLogoUrl = (value?: string | null) =>
  Boolean(value && /^https?:\/\//i.test(value));

const Settings: React.FC = () => {
  const ctx = useCtx();
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoURL, setLogoURL] = useState<string | null>(null);

  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user.uid,
  );

  useEffect(() => {
    let isMounted = true;

    const loadLogo = async () => {
      if (!userClient?.logoUrl) {
        if (isMounted) setLogoURL(null);
        return;
      }

      if (isDirectLogoUrl(userClient.logoUrl)) {
        if (isMounted) setLogoURL(userClient.logoUrl);
        return;
      }

      try {
        const url = await getDownloadURL(ref(storage, userClient.logoUrl));
        if (isMounted) setLogoURL(url);
      } catch (error) {
        console.error("Nie udało się pobrać logo szkoły:", error);
        if (isMounted) setLogoURL(null);
      }
    };

    void loadLogo();

    return () => {
      isMounted = false;
    };
  }, [userClient?.id, userClient?.logoUrl]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
      })
      .catch((err) => {
        console.error("Błąd kopiowania: ", err);
      });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userClient) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `logos/${userClient.id}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setLogoURL(url);

      const clientDocRef = doc(firestore, "clients", userClient.id);
      await updateDoc(clientDocRef, { logoUrl: url });
      try {
        await cacheCertificateLogoFromFile(userClient.id, url, file);
      } catch (cacheError) {
        console.warn("Nie udało się przygotować logo do PDF:", cacheError);
      }
    } catch (err) {
      console.error("Błąd uploadu logo:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto my-2 max-w-4xl space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-start">
        <div className="space-y-5">
          {/* ── Section 1: Access token ── */}
          <div className="rounded-[24px] border border-white/50 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
                  Token dostępu
                </p>
                <p className="mt-1.5 text-lg font-bold text-slate-800 sm:text-xl">
                  {userClient?.token}
                </p>
              </div>

              {userClient?.token && (
                <button
                  onClick={() => copyToClipboard(userClient.token)}
                  className={`flex items-center gap-2 rounded-full px-4 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                  style={getActionButtonStyle(copied ? "emerald" : "slate")}
                  title={copied ? "Skopiowano!" : "Kopiuj token"}
                >
                  {copied ? (
                    <>
                      <Check
                        size={16}
                        strokeWidth={2.4}
                        className="text-white"
                      />
                      <span>Skopiowano</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} strokeWidth={2.2} />
                      <span>Kopiuj</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-amber-200/70 bg-amber-50/60 px-4 py-3">
              <span className="mt-0.5 shrink-0 text-amber-500">⚠️</span>
              <p className="text-sm leading-relaxed text-amber-800">
                Chcesz zmienić token? Skontaktuj się z nami przed pierwszym
                użyciem przez ucznia lub lektora — potem zmiana nie będzie
                możliwa.
              </p>
            </div>
          </div>

          {/* Logo upload */}
          <div className="rounded-[24px] border border-white/50 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
              Logo szkoły
            </p>

            <div className="mt-4 flex items-center gap-4">
              {logoURL ? (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-200/60 bg-white/80 p-2">
                  <img
                    src={logoURL}
                    alt="Logo szkoły"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 text-slate-300">
                  <ImageIcon size={24} strokeWidth={1.5} />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-500">
                  {logoURL
                    ? "Logo zostało wgrane."
                    : "Wgraj logo swojej szkoły."}
                </p>
                <label
                  htmlFor="logo-upload"
                  className={`mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                  style={getActionButtonStyle("slate")}
                >
                  <Upload size={16} strokeWidth={2.1} />
                  <span>{logoURL ? "Zmień logo" : "Wgraj logo"}</span>
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </div>
            </div>

            {uploading && (
              <p className="mt-3 text-sm text-slate-500">
                Trwa przesyłanie...
              </p>
            )}
          </div>
        </div>

        {/* Placement tests */}
        <div className="rounded-[24px] border border-white/50 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
            Testy poziomujące — Dorośli
          </p>

          <div className="mt-4 space-y-2">
            <a
              href="Test_poziomujący_pisemny_Direct_Press.pdf"
              download
              className="flex items-center justify-between rounded-2xl border border-slate-200/50 bg-white/50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white/80"
            >
              <span>Test pisemny</span>
              <Download
                size={17}
                strokeWidth={2.2}
                className="text-slate-400"
              />
            </a>
            <a
              href="Test_poziomujący_ustny_Direct_Press.pdf"
              download
              className="flex items-center justify-between rounded-2xl border border-slate-200/50 bg-white/50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white/80"
            >
              <span>Test ustny</span>
              <Download
                size={17}
                strokeWidth={2.2}
                className="text-slate-400"
              />
            </a>
            <a
              href="Klucz_do_testu_pisemnego_Direct_Press.pdf"
              download
              className="flex items-center justify-between rounded-2xl border border-slate-200/50 bg-white/50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white/80"
            >
              <span>Klucz do testu pisemnego</span>
              <Download
                size={17}
                strokeWidth={2.2}
                className="text-slate-400"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
